import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'
import { AppState } from 'src/state/store'

// LP Tier-3 supplement (short corporate form). A trimmed version of the full LP
// questionnaire: only Section A (= full-form E "account use"), B (= F "source of
// funds & wealth") and C (= G1 authority + G4 declarations). Its own slice keeps
// the short supplement's progress and answers isolated from a full LP session
// for the same entity. Completed kept at 10 to mirror the other fintwin slices.
export enum FintwinCorporateT3StepEnum {
  AccountUse = 1, // Section A - how the account will be used (full-form E)
  SourceOfFunds = 2, // Section B - source of funds & wealth (full-form F)
  // Optional supporting documents, before the declarations. Declarations
  // shifted 3 -> 4 to make room (mirrors the other fintwin flows).
  Documents = 3,
  Declarations = 4, // Section C - declarations & consent (full-form G1 + G4)
  Completed = 10,
}

export interface FintwinCorporateT3VerificationState {
  currentStep: FintwinCorporateT3StepEnum
  availableStep: FintwinCorporateT3StepEnum
  isCompleted: boolean
  formValues: Record<string, unknown>
  // Id of the user this in-progress supplement belongs to (see claim* below).
  ownerId: number | null
}

export const initialFintwinCorporateT3VerificationState: FintwinCorporateT3VerificationState =
  {
    currentStep: FintwinCorporateT3StepEnum.AccountUse,
    availableStep: FintwinCorporateT3StepEnum.AccountUse,
    isCompleted: false,
    formValues: {},
    ownerId: null,
  }

const hydrate = createAction<AppState>(HYDRATE)

const fintwinCorporateT3VerificationSlice = createSlice({
  name: 'fintwinCorporateT3Verification',
  initialState: initialFintwinCorporateT3VerificationState,
  reducers: {
    changeCurrentStep(
      state,
      action: PayloadAction<FintwinCorporateT3StepEnum>
    ) {
      state.currentStep = action.payload
    },
    changeAvailableStep(
      state,
      action: PayloadAction<FintwinCorporateT3StepEnum>
    ) {
      state.availableStep = action.payload
    },
    progressToStep(state, action: PayloadAction<FintwinCorporateT3StepEnum>) {
      const nextStep = action.payload

      state.currentStep = nextStep

      if (nextStep > state.availableStep) {
        state.availableStep = nextStep
      }
    },
    addFormValues(state, action: PayloadAction<Record<string, unknown>>) {
      state.formValues = { ...state.formValues, ...action.payload }
    },
    completeT3Verification(state) {
      state.isCompleted = true
      state.currentStep = FintwinCorporateT3StepEnum.Completed
      state.availableStep = FintwinCorporateT3StepEnum.Completed
    },
    // Same owner resumes; a different/unknown owner gets a clean supplement.
    claimCorporateT3Owner(state, action: PayloadAction<number>) {
      if (state.ownerId !== action.payload) {
        return {
          ...initialFintwinCorporateT3VerificationState,
          ownerId: action.payload,
        }
      }
    },
    flushFintwinCorporateT3() {
      return initialFintwinCorporateT3VerificationState
    },
  },
  extraReducers: builder => {
    builder.addCase(hydrate, state => state)
  },
})

const { actions, reducer } = fintwinCorporateT3VerificationSlice

export const {
  changeCurrentStep,
  changeAvailableStep,
  progressToStep,
  addFormValues,
  completeT3Verification,
  claimCorporateT3Owner,
  flushFintwinCorporateT3,
} = actions

export default reducer
